import { useState } from "react";
import { SurgeryTypeDto, surgeryTypeFormData, surgeryTypeIdKey } from "../types/surgeryType";
import { surgeryTypeService } from "../services/surgeryTypeService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const useSurgeryTypes = () => {
  // 1. Το GET γίνεται αυτόματα με το useApi, cache και signal
  const {
    data: surgeryTypes = [],
    loading: isFetching,
    error: getError,
    refetch: fetchSurgeryTypes,
    invalidateCache,
  } = useApi<SurgeryTypeDto[]>("surgery-types", (signal) => surgeryTypeService.getAll({}, signal));

  // 2. Το CREATE τυλίγεται στο καθαρό useMutation
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newSurgeryType: SurgeryTypeFormData) => surgeryTypeService.create(newSurgeryType));

  // 2.1. Το UPDATE τυλίγεται στο καθαρό useMutation
  const {
    execute: updateExecution,
    loading: isUpdating,
    error: updateError,
  } = useMutation(({ id, data }: { id: number; data: SurgeryTypeFormData }) => 
    surgeryTypeService.update(id, data)
  );

  // 3. Το DELETE τυλίγεται στο καθαρό useMutation
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => surgeryTypeService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "success",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createSurgeryType = async (newSurgeryType: SurgeryTypeFormData): Promise<void> => {
    try {
      await createExecution(newSurgeryType);
      
      // Καθαρισμός cache για να φέρει τα νέα δεδομένα στην επόμενη προβολή
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Ο τύπος χειρουργείου αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      
      fetchSurgeryTypes(true); // Άμεση ανανέωση λίστας
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const updateSurgeryType = async (id: number, updatedData: SurgeryTypeFormData): Promise<void> => {
    try {
      await updateExecution({ id, data: updatedData });
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ενημέρωση ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      fetchSurgeryTypes(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα ενημέρωσης";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const deleteSurgeryTypes = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      
      // Καθαρισμός cache
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      
      fetchSurgeryTypes(true); // Άμεση ανανέωση λίστας
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    surgeryTypes,
    isLoading: isFetching || isCreating || isUpdating || isDeleting,
    error: getError || createError || updateError || deleteError,
    fetchSurgeryTypes: () => fetchSurgeryTypes(true),
    createSurgeryType,
    updateSurgeryType,
    deleteSurgeryTypes,
    modalState,
    closeModal,
  };
};